<template>
    <v-main>
        <div class="getting-started">
            <div
                class="getting-started-container elevation-5"
                :class="{
                    wfull: $vuetify.breakpoint.smAndDown,
                    hfull: $vuetify.breakpoint.smAndDown
                }"
            >
                <div v-if="!$vuetify.breakpoint.xs" class="steps">
                    <div class="steps-title">{{ $t('general.get_started') }}!</div>
                    <v-tabs
                        v-model="curStep"
                        color="white"
                        background-color="transparent"
                        dark
                        vertical
                    >
                        <v-tab
                            v-for="(step, index) in steps"
                            v-show="step.enabled"
                            :key="index"
                            class="step-title"
                            :disabled="curStep !== index"
                        >
                            {{ $t(`get_started.${step.name}`) }}
                        </v-tab>
                    </v-tabs>
                </div>

                <div class="forms-container">
                    <v-btn
                        class="sign-out"
                        dark
                        icon
                        @click="logoutTenant"
                    >
                        <v-icon>fal fa-sign-out</v-icon>
                    </v-btn>

                    <v-tabs-items v-model="curStep" dark>

                        <!-- Current Step Component -->
                        <v-tab-item
                            v-for="(step, index) in steps"
                            v-show="step.enabled"
                            :key="index"
                        >
                            <component
                                :is="stepComponent(index)"
                                :ref="`component${index}`"
                                @finish="finishStep"
                            />
                        </v-tab-item>

                        <!-- Finish -->
                        <v-tab-item>
                            <div class="finish">
                                <v-scale-transition>
                                    <div v-show="showSuccessMessage" class="success-msg">
                                        {{ $t('get_started.finished') }}
                                    </div>
                                </v-scale-transition>
                                <v-fab-transition>
                                    <div v-show="showSuccessMessage" class="success-icon">
                                        <v-icon color="success" size="50">fal fa-check</v-icon>
                                    </div>
                                </v-fab-transition>
                            </div>
                        </v-tab-item>

                    </v-tabs-items>
                </div>
            </div>
        </div>
    </v-main>
</template>

<script>
import * as mutationTypes from '@/store/mutationTypes';
import ApiService from '@/services/ApiService'
import TerminalService from '@/services/TerminalService'
import TenantService from '@/services/TenantService'
import CashRegisterService from '@/services/CashRegisterService'
import VenueSetup from '@/views/shared/gettingStarted/VenueSetup'
import EinvoiceSetup from '@/views/shared/gettingStarted/EinvoiceSetup'
import ContractSetup from '@/views/shared/gettingStarted/ContractSetup'
import ProductsSetup from '@/views/shared/gettingStarted/ProductsSetup'
import TablesSetup from '@/views/shared/gettingStarted/TablesSetup'
import PrintersSetup from '@/views/shared/gettingStarted/PrintersSetup'
import CashRegisterSetup from '@/views/shared/gettingStarted/CashRegisterSetup'
import { mapGetters } from 'vuex';

export default {
    components: {
        VenueSetup,
        EinvoiceSetup,
        ContractSetup,
        ProductsSetup,
        TablesSetup,
        PrintersSetup,
        CashRegisterSetup,
    },

    created() {
        ApiService.get('/getstarted/load').then(response => {
            this.disabledSteps = response.data.disabled_steps
        })

        const lastFinishedStep = this.steps.findIndex(s => this.tenant && s.name === this.tenant.getstarted_status)
        this.curStep = isNaN(parseInt(lastFinishedStep)) ? 0 : lastFinishedStep + 1

        while(this.steps[this.curStep] && !this.steps[this.curStep].enabled) {
            this.curStep++
        }

        if(this.curStep === this.steps.length) {
            this.finish()
        }
    },

    data() {
        return {
            curStep: 0,
            showSuccessMessage: false,
            disabledSteps: []
        };
    },

    computed: {
        ...mapGetters(['modules']),

        /**
         * Get Started steps
         */
        steps() {
            const venue = this.tenant ? this.tenant.venue : null

            return [{
                name: 'venue_setup',
                enabled: !this.disabledSteps.includes('venue_setup')
            }, {
                name: 'contract_setup',
                enabled: this.tenant
                    && !this.disabledSteps.includes('contract_setup')
                    && this.tenant.status !== 'permanently_active'
                    && process.env.VUE_APP_ENV === 'production'
            }, {
                name: 'einvoice_setup',
                enabled: !this.disabledSteps.includes('einvoice_setup') && this.modules.einvoices
            }, {
                name: 'products_setup',
                enabled: !this.disabledSteps.includes('products_setup')
            }, {
                name: 'tables_setup',
                enabled: !this.disabledSteps.includes('tables_setup') && !this.isAllegra
            }, {
                name: 'printers_setup',
                enabled: !this.disabledSteps.includes('printers_setup')
            }, {
                name: 'cash_register_setup',
                enabled: !this.disabledSteps.includes('cash_register_setup')
                    && venue
                    && !venue.settings.cash_register.single
                    && !(locale === 'sr' && this.device.platform === 'web')
            }]
        },
    },

    methods: {

        /**
         * Current Step Component
         */
        stepComponent(index) {
            let component = _.camelCase(this.steps[index].name)
            return component[0].toUpperCase() + component.substr(1)
        },

        /**
         * Finish current step
         */
        finishStep() {
            this.curStep++

            let step = this.steps[this.curStep]

            while(step && !step.enabled) {
                this.curStep++
                step = this.steps[this.curStep]
            }

            if(this.curStep === this.steps.length) {
                this.finish()
            }
        },

        /**
         * Finish the Getting Started process
         */
        finish() {
            ApiService.post('/getstarted/complete', {}).then(response => {
                this.$store.commit(mutationTypes.SET_TENANT, { tenant: response.data.tenant });

                if(response.data.cash_register) {
                    CashRegisterService.setCashRegister(response.data.cash_register);
                }

                setTimeout(() => (this.showSuccessMessage = true), 500);
                setTimeout(() => (this.$router.push({ name: 'userLogin' }).catch(err => {})), 3000);
            });
        },

        /**
         * Log out Tenant
         */
        logoutTenant() {
            TenantService.logout();
            this.$router.push({ name: 'tenantLogin' }).catch(err => {});
        },
    },
};
</script>

<style lang="scss" scoped>
.v-main {
    overflow-x: auto;

    .getting-started {
        background-size: cover;
        background-position: bottom left;
        background-image: url('/img/background.png');
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        min-width: 300px;
        color: white;

        .getting-started-container {
            display: flex;

            ::v-deep .steps {
                text-align: center;
                width: 270px;
                min-height: 640px;
                padding: 15px;
                display: flex;
                flex-direction: column;
                align-items: center;
                background: #282b38;

                .v-slide-group__content {
                    max-width: 100%;
                }

                .step-title {
                    justify-content: flex-start;
                    white-space: normal;
                    text-align: left;
                    line-height: 1.4;
                }

                .steps-title {
                    padding: 5px;
                    margin-top: 5px;
                    margin-bottom: 20px;
                    font-size: 20px;
                }
            }

            .forms-container {
                position: relative;
                color: white;
                background: #1f212b;
                padding: 20px;
                width: 100%;
                min-width: 550px;
                max-width: 550px;
                min-height: 650px;

                @media screen and (max-width: 960px) {
                    min-width: inherit;
                    max-width: 100%;
                }

                .sign-out {
                    position: absolute;
                    right: 15px;
                    top: 15px;
                    z-index: 1;
                }

                .v-tabs-items {
                    background: inherit;
                    height: 100%;

                    .v-window-item {
                        height: 100%;

                        .finish {
                            height: 100%;
                            width: 100%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            flex-direction: column;

                            .success-msg {
                                max-width: 290px;
                                text-align: center;
                                line-height: 1.5;
                                margin: 20px;
                                color: white;
                                font-size: 32px;
                            }

                            .success-icon {
                                height: 100px;
                                width: 100px;
                                border: 5px solid #4baf4f;
                                border-radius: 50%;
                                color: #4baf4f;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
